import { FULL_DAY_STREAM_OBJECT_TYPE, VideoRecording } from '../../../models/common/video-playback.model';
import { StreamStateFields, effectiveStreamStatus, isLiveStream, withStreamState } from './video-stream.util';

/** A finished recording of a Full Day Stream, numbered from 1 in the order it was streamed. */
export interface FullDayStreamPart {
  readonly number: number;
  readonly recording: VideoRecording;
}

export interface FullDayStreamVideo extends StreamStateFields {
  id: number | string;
  object_type?: string | null;
  recordings?: readonly VideoRecording[] | null;
  asset_playback_id?: string | null;
}

/** A row of the Watch Centre grid: the video itself, or one recorded part of a Full Day Stream. */
export type VideoCardItem<T extends FullDayStreamVideo> = T & {
  readonly card_key: string;
  readonly part?: FullDayStreamPart;
};

/**
 * Recorded parts of a video. While the stream is live, a last recording IVS hasn't finished (no duration yet)
 * is the session in progress, not a part; once paused or ended, every recording is a part.
 */
export function recordedParts(video: FullDayStreamVideo | null | undefined): FullDayStreamPart[] {
  if (video?.object_type !== FULL_DAY_STREAM_OBJECT_TYPE) {
    return [];
  }
  const recordings = video.recordings ?? [];
  const inProgress = isLiveStream(video.stream_status ?? undefined) && recordings.at(-1)?.duration_ms == null;
  const finished = inProgress ? recordings.slice(0, -1) : recordings;
  return finished.map((recording, index) => ({ number: index + 1, recording }));
}

/**
 * One card per recorded part of each Full Day Stream (a live or paused stream also keeps its own card first),
 * in the order the API returned the videos. Every other video stays a single card.
 */
export function expandFullDayStreamParts<T extends FullDayStreamVideo>(videos: readonly T[]): VideoCardItem<T>[] {
  return videos.flatMap((video) => {
    const videoCard: VideoCardItem<T> = { ...video, card_key: String(video.id) };
    const parts = recordedParts(video);
    if (!parts.length) {
      return [videoCard];
    }
    const partCards = parts.map((part): VideoCardItem<T> => ({
      ...withStreamState(video, { stream_status: 'S', playback_state: null }),
      card_key: `${video.id}-${part.number}`,
      part,
      asset_playback_id: part.recording.playback_url,
    }));
    return effectiveStreamStatus(video) === 'L' ? [videoCard, ...partCards] : partCards;
  });
}

/** The "Daywise Streaming" tab's object type; that tab lists Full Day Streams too. */
export const DAYWISE_STREAMING_OBJECT_TYPE = 'OTHERS';

/** get_videos filter for a Watch Centre tab's object type (null = every type). */
export function videoTabFilter(objectType: string | null): { object_type: string | null; object_types: string[] | null } {
  return objectType === DAYWISE_STREAMING_OBJECT_TYPE
    ? { object_type: null, object_types: [DAYWISE_STREAMING_OBJECT_TYPE, FULL_DAY_STREAM_OBJECT_TYPE] }
    : { object_type: objectType, object_types: null };
}

/** "02h 15m"; empty while the duration isn't known yet. */
export function formatPartDuration(durationMs: number | null | undefined): string {
  if (durationMs == null) {
    return '';
  }
  const totalMinutes = Math.round(durationMs / 60000);
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
  const minutes = String(totalMinutes % 60).padStart(2, '0');
  return `${hours}h ${minutes}m`;
}
